import { useEffect, useRef, useState, type ReactNode } from 'react';
import { formatDateTime, formatTime } from '../time.js';
import { PHASES, type PhaseId, type ViewId } from './model.js';

import { Glyph, ICON_FOR, type IconName } from '../icons.js';
import { MARK } from '../mark.js';

/** One view in the switcher. Items come from the registry; the shell only draws them. */
export interface ShellView {
  id: ViewId;
  name: string;
  desc: string;
  items: readonly { label: string; href: string }[];
}

export interface ShellPhase {
  /** Null until the year's dates exist — the strip then says so instead of guessing. */
  id: PhaseId | null;
  /** One line per view home: "Setup week starts in 41 days." */
  countdown: string;
  /** Amber when the onboarding packet has an expired item on a chair (People Part 7). */
  tone?: 'amber' | null | undefined;
}

export interface ShellIdentity {
  initial: string;
  name: string;
  role: string;
  /** Views this person's capabilities reach. An area chair has one and sees no switcher. */
  views: readonly ViewId[];
  /** Rule 6: a director's home is Decide. The count is the badge; null is NO DATA. */
  decide: number | null;
  /** The bell: unread notices. Null reads NO DATA until Comms lands. */
  unread: number | null;
}

/** The left rail: the open section's name, then its screens indented, the current one lit. */
export interface ShellRail {
  /** The section the screens belong to. */
  section?: string | undefined;
  /** `why`: the hover reason the drawing gives each entry (Rebuild 34–36's Money rail). */
  screens: readonly { label: string; href: string; current: boolean; why?: string | undefined }[];
}

export interface ShellProps {
  views: readonly ShellView[];
  view: ViewId;
  /** The lit item in the section rail. */
  section?: string | undefined;
  /**
   * A page of its own, inside the shell but under no view: Decide and What's new. The bar, the
   * phase strip and the identity stay; the section rail and the left rail do not, because they
   * belong to a view this page is not in (walk fixes 2, item 5).
   */
  standalone?: { name: string } | undefined;
  /** The current screen, for the breadcrumb. */
  screen?: string | undefined;
  rail?: ShellRail | undefined;
  phase: ShellPhase;
  who: ShellIdentity;
  /** Set when a director or volunteer chair is viewing as a chair — read-only, audited. */
  viewingAs?: { name: string; area: string; endsAt: Date | string } | undefined;
  /** Every address goes through here, so the app's router sees it. */
  onGo: (href: string) => void;
  onViewAs?: (() => void) | undefined;
  onStopViewingAs?: (() => void) | undefined;
  onSignOut?: (() => void) | undefined;
  /** Your account: where a person sets their own password (alpha.41). */
  onAccount?: (() => void) | undefined;
  /** What Jump-to shows. The shell draws the box; the app fills it. */
  jump?: ((query: string, close: () => void) => ReactNode) | undefined;
  /** What the bell opens: the notices, one per event, mark done. The shell draws the box; the app fills it. */
  bell?: ((close: () => void) => ReactNode) | undefined;
  /** The status bar: version · build · deployed when; a tap opens What's new (People Part 6). */
  status?:
    { version: string; build: string; deployedAt: Date | string | null; href: string } | undefined;
  /** A newer build is live: the bar says so and offers Reload; never automatic. */
  newVersion?: { version: string; onReload: () => void } | undefined;
  offline?: boolean;
  children?: ReactNode;
}

/** E05: the rail's head draws its view's own icon from the one set; the rest keep the grid. */
const VIEW_ICON: Partial<Record<ViewId, IconName>> = {
  money: ICON_FOR.Money,
  volunteers: ICON_FOR.Volunteers,
  ops: ICON_FOR['Festival Ops'],
};
const railIcon = (view: ViewId): ReactNode => <Glyph name={VIEW_ICON[view] ?? 'layout-grid'} />;

/**
 * The one shell, as `proto/cider-shell.js` draws it: the bar (mark · CIDER / FESTIVAL OPS ·
 * switcher · Jump-to · Decide · bell · identity), the phase strip with its sentence, the
 * section rail, the breadcrumb, then the left rail beside the page. Phase never changes the
 * items (Nav rule 4). Every operator page mounts this; nobody copies it.
 */
export function Shell(props: ShellProps): ReactNode {
  const { views, view, section, screen, rail, phase, who, viewingAs, onGo, children } = props;
  const [open, setOpen] = useState<'views' | 'jump' | 'who' | 'bell' | null>(null);
  const [query, setQuery] = useState('');
  const jumpInput = useRef<HTMLInputElement>(null);
  const current = views.find((v) => v.id === view);
  const visible = views.filter((v) => who.views.includes(v.id));
  const canSwitch = visible.length > 1;

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => (o === 'jump' ? null : 'jump'));
      }
      if (e.key === 'Escape') setOpen(null);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);
  useEffect(() => {
    if (open === 'jump') jumpInput.current?.focus();
  }, [open]);

  const go = (href: string): void => {
    setOpen(null);
    onGo(href);
  };
  const close = (): void => {
    setOpen(null);
  };
  const link = (
    href: string,
    label: ReactNode,
    className?: string,
    extra: Record<string, unknown> = {},
  ): ReactNode => (
    <a
      href={href}
      className={className}
      {...extra}
      onClick={(e) => {
        e.preventDefault();
        go(href);
      }}
    >
      {label}
    </a>
  );

  return (
    <div className="cider-shell" data-view={view} data-phase={phase.id ?? 'none'}>
      <div className="cider-shell__chrome">
        <header className="cider-shell__bar">
          <img className="cider-shell__mark" src={MARK} alt="" />
          <div className="cider-shell__brand" aria-label="CIDER">
            <span className="cider-shell__brand-name">CIDER</span>
            <span className="cider-shell__brand-sub">APPLE FEST</span>
          </div>

          {canSwitch ? (
            <button
              type="button"
              className="cider-shell__switcher"
              aria-haspopup="menu"
              aria-expanded={open === 'views'}
              onClick={() => {
                setOpen(open === 'views' ? null : 'views');
              }}
            >
              <span className={`cider-shell__tag cider-shell__tag--${view}`} />
              <span>{props.standalone ? 'HOME' : (current?.name ?? 'CIDER')}</span>
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                aria-hidden="true"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
          ) : (
            <div className="cider-shell__switcher cider-shell__switcher--single">
              <span className={`cider-shell__tag cider-shell__tag--${view}`} />
              <span>{props.standalone ? 'HOME' : current?.name}</span>
            </div>
          )}

          <button
            type="button"
            className="cider-shell__jump"
            aria-keyshortcuts="Meta+K"
            onClick={() => {
              setOpen(open === 'jump' ? null : 'jump');
            }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" />
            </svg>
            <span>Jump to anything…</span>
            <kbd>⌘K</kbd>
          </button>

          {who.views.length > 1 || who.decide !== null ? (
            <button
              type="button"
              className="cider-shell__decide"
              onClick={() => {
                go('/decide');
              }}
            >
              DECIDE <b>{who.decide ?? '—'}</b>
            </button>
          ) : (
            <span className="cider-shell__spacer" />
          )}

          <button
            type="button"
            className="cider-shell__bell"
            title={
              who.unread === null
                ? 'NO DATA — the notices could not be read'
                : `${who.unread} unread`
            }
            aria-label={`Notices${who.unread === null ? '' : `, ${String(who.unread)} unread`}`}
            aria-haspopup="dialog"
            aria-expanded={open === 'bell'}
            onClick={() => {
              setOpen(open === 'bell' ? null : 'bell');
            }}
          >
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#c8d4ec"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="M18 8a6 6 0 10-12 0c0 7-3 8-3 8h18s-3-1-3-8" />
              <path d="M13.7 21a2 2 0 01-3.4 0" />
            </svg>
            <span
              className="cider-shell__bell-count"
              data-nodata={who.unread === null ? 'true' : undefined}
            >
              {who.unread ?? 0}
            </span>
          </button>

          <button
            type="button"
            className="cider-shell__identity"
            aria-haspopup="menu"
            aria-expanded={open === 'who'}
            onClick={() => {
              setOpen(open === 'who' ? null : 'who');
            }}
          >
            <span className="cider-shell__avatar" aria-hidden="true">
              {who.initial}
            </span>
            <span className="cider-shell__who">
              <b>{who.name}</b>
              <span>{who.role} ▾</span>
            </span>
          </button>
        </header>

        <div className="cider-shell__phase" aria-label="Phase" data-tone={phase.tone ?? undefined}>
          <span className="cider-shell__phase-label">PHASE</span>
          <span className="cider-shell__chips">
            {PHASES.map(([id, label]) => (
              <span
                key={id}
                className={[
                  'cider-shell__chip',
                  id === phase.id ? 'cider-shell__chip--on' : '',
                ].join(' ')}
              >
                {label}
              </span>
            ))}
          </span>
          <span className="cider-shell__countdown">{phase.countdown}</span>
        </div>

        {current && !props.standalone ? (
          <nav aria-label={`${current.name} sections`} className="cider-shell__items">
            {current.items.map((item) =>
              link(
                item.href,
                item.label,
                item.label === section ? 'cider-shell__item--on' : undefined,
                { 'aria-current': item.label === section ? 'page' : undefined },
              ),
            )}
          </nav>
        ) : null}

        <div className="cider-shell__crumb" aria-label="Where you are">
          <span className={`cider-shell__tag cider-shell__tag--small cider-shell__tag--${view}`} />
          <span className="cider-shell__crumb-view">
            {props.standalone ? 'HOME' : current?.name}
          </span>
          {props.standalone ? (
            <>
              <span>→</span>
              <span className="cider-shell__crumb-section">{props.standalone.name}</span>
            </>
          ) : null}
          {section ? (
            <>
              <span>→</span>
              <span className="cider-shell__crumb-section">{section}</span>
            </>
          ) : null}
          {screen ? (
            <>
              <span>→</span>
              <b>{screen}</b>
            </>
          ) : null}
        </div>

        {viewingAs ? (
          <div className="cider-shell__banner" role="status">
            Viewing as {viewingAs.name} · {viewingAs.area} — read-only, audited, ends{' '}
            {formatTime(viewingAs.endsAt)}
            {props.onStopViewingAs ? (
              <button type="button" onClick={props.onStopViewingAs}>
                Stop
              </button>
            ) : null}
          </div>
        ) : null}
        {props.offline ? (
          <div className="cider-shell__banner cider-shell__banner--offline" role="status">
            You are offline. This is the cached shell — nothing on it is live.
          </div>
        ) : null}

        {open === 'views' ? (
          <div className="cider-shell__menu cider-shell__menu--views" role="menu">
            <div className="cider-shell__menu-head">
              SWITCH VIEW — {visible.length + 1} AVAILABLE TO {who.name.toUpperCase()}
            </div>
            {/*
              Home is the first item, above the six (James, Sep 24). It is where a director lands
              and what the bar's DECIDE button opens; a menu that leaves it out cannot say where
              you are when you are on it.
            */}
            <a
              href="/decide"
              role="menuitem"
              className={props.standalone ? 'cider-shell__menu-row--on' : undefined}
              onClick={(e) => {
                e.preventDefault();
                go('/decide');
              }}
            >
              <span className="cider-shell__tag cider-shell__tag--home" />
              <span>
                <span className="cider-shell__menu-name">HOME</span>
                <span className="cider-shell__menu-desc">What is waiting on you</span>
              </span>
              {props.standalone ? <span className="cider-shell__here">HERE</span> : null}
            </a>
            {visible.map((v) => (
              <a
                key={v.id}
                href={`/${v.id}`}
                role="menuitem"
                className={
                  v.id === view && !props.standalone ? 'cider-shell__menu-row--on' : undefined
                }
                onClick={(e) => {
                  e.preventDefault();
                  go(`/${v.id}`);
                }}
              >
                <span className={`cider-shell__tag cider-shell__tag--${v.id}`} />
                <span>
                  <span className="cider-shell__menu-name">{v.name}</span>
                  <span className="cider-shell__menu-desc">{v.desc}</span>
                </span>
                {v.id === view && !props.standalone ? (
                  <span className="cider-shell__here">HERE</span>
                ) : null}
              </a>
            ))}
            {props.onViewAs ? (
              <button
                type="button"
                role="menuitem"
                className="cider-shell__menu-link"
                onClick={() => {
                  close();
                  props.onViewAs?.();
                }}
              >
                View as a chair
              </button>
            ) : null}
          </div>
        ) : null}

        {open === 'jump' ? (
          <div
            className="cider-shell__menu cider-shell__menu--jump"
            role="dialog"
            aria-label="Jump to"
          >
            <div className="cider-shell__menu-head">JUMP TO — PLAIN WORDS, ANY VIEW</div>
            <input
              ref={jumpInput}
              className="cider-shell__jump-input"
              value={query}
              placeholder="a screen, a person, a vendor, a shift…"
              onChange={(e) => {
                setQuery(e.target.value);
              }}
            />
            {props.jump ? props.jump(query, close) : null}
          </div>
        ) : null}

        {open === 'bell' ? (
          <div
            className="cider-shell__menu cider-shell__menu--bell"
            role="dialog"
            aria-label="Notices"
          >
            <div className="cider-shell__menu-head">
              NOTICES — {who.unread === null ? 'NO DATA' : `${String(who.unread)} TO DO`}
            </div>
            {props.bell ? props.bell(close) : null}
          </div>
        ) : null}

        {open === 'who' ? (
          <div className="cider-shell__menu cider-shell__menu--who" role="menu">
            <div className="cider-shell__menu-head">{who.role.toUpperCase()}</div>
            {props.onAccount ? (
              <button
                type="button"
                role="menuitem"
                className="cider-shell__menu-link"
                onClick={() => {
                  close();
                  props.onAccount?.();
                }}
              >
                Your account
              </button>
            ) : null}
            {props.onSignOut ? (
              <button
                type="button"
                role="menuitem"
                className="cider-shell__menu-link"
                onClick={() => {
                  close();
                  props.onSignOut?.();
                }}
              >
                Sign out
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="cider-shell__body">
        {rail && current ? (
          <nav className="cider-shell__rail" aria-label={`${current.name} screens`}>
            <div className="cider-shell__rail-head">
              <span className="cider-shell__rail-icon">{railIcon(current.id)}</span>
              <span className="cider-shell__rail-title">
                <span className="cider-shell__rail-view">{current.name}</span>
              </span>
            </div>
            {rail.section ? <div className="cider-shell__rail-section">{rail.section}</div> : null}
            {rail.screens.map((s) =>
              link(
                s.href,
                s.label,
                ['cider-shell__rail-item', s.current ? 'cider-shell__rail-item--on' : ''].join(' '),
                { 'aria-current': s.current ? 'page' : undefined, title: s.why },
              ),
            )}
          </nav>
        ) : null}
        <main className="cider-shell__main">{children}</main>
      </div>
      {props.newVersion ? (
        <div className="cider-shell__newversion" role="status">
          A new version is ready — {props.newVersion.version}.{' '}
          <button type="button" onClick={props.newVersion.onReload}>
            Reload
          </button>
          <span className="cider-shell__newversion-note">What you are typing is kept.</span>
        </div>
      ) : null}
      {props.status ? (
        <footer className="cider-shell__status" aria-label="Status bar">
          <a
            href={props.status.href}
            onClick={(e) => {
              e.preventDefault();
              go(props.status?.href ?? '/');
            }}
          >
            CIDER {props.status.version} · build {props.status.build}
            {props.status.deployedAt
              ? ` · deployed ${formatDateTime(props.status.deployedAt)}`
              : ''}
            {' · What’s new'}
          </a>
        </footer>
      ) : null}
    </div>
  );
}
